import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { AlertController, IonicModule, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { AdvancedModelSettingsService } from '../../../core/services/advanced-model-settings.service';
import { AuthService } from '../../../core/services/auth.service';
import {
  BroadcastMessage,
  BroadcastTranslationService,
} from '../../../core/services/broadcast-translation.service';
import { ConversationHistoryService } from '../../../core/services/conversation-history.service';
import { ContinuousConversationService } from '../../../core/services/continuous-conversation.service';
import { LiveTranslationSettingsService } from '../../../core/services/live-translation-settings.service';
import {
  LiveTranslationSegment,
  LiveTranslationService,
  LiveTranslationSession,
} from '../../../core/services/live-translation.service';
import { TranslationService } from '../../../core/services/translation.service';
import { TranslatorService } from '../../../core/services/translator.service';
import { ConversationHistoryEntry } from '../../../shared/models';

@Component({
  selector: 'app-broadcast-translation-modal',
  templateUrl: './broadcast-translation-modal.component.html',
  styleUrls: ['./broadcast-translation-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
})
export class BroadcastTranslationModalComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() isOpen = false;
  @Input() sourceLanguage: string = 'en';
  @Input() targetLanguage: string = 'en';
  @Input() conversationTopic?: string; // Conversation topic from main page for better translation context
  @Output() close = new EventEmitter<void>();
  @Output() showBuyWords = new EventEmitter<void>();

  // Mode: 'speaker' | 'listener'
  mode: 'speaker' | 'listener' = 'speaker';

  // Speaker mode
  selectedSourceLanguage: string = 'en';
  selectedTargetLanguage: string = 'en';
  extraLanguages: string[] = []; // Up to 3 extra languages
  availableLanguages: { code: string; name: string }[] = [];
  broadcastCode: string = '';
  isBroadcasting = false;

  // Listener mode
  enteredCode: string = '';
  selectedListenerLanguage: string = 'en';
  isJoined = false;
  availableListenerLanguages: string[] = []; // Languages available in the current broadcast session

  // Common
  messages: BroadcastMessage[] = [];
  errorMessage = '';
  recording = false;
  lectureMode = false; // Lecture mode: record every 20 seconds instead of pause-based
  useAdvancedModel = false;

  // Speaker source language transcriptions (for speaker to verify what they said)
  speakerSourceTexts: Array<{ text: string; timestamp: Date }> = [];

  // Audio feature for source text playback (speaker mode) and message playback (listener mode)
  audioEnabled = signal<boolean>(false);
  private audioQueue: Array<{ audioUrl: string; text: string }> = [];
  private isPlayingAudio = false;
  private currentAudio?: HTMLAudioElement;
  private processedSourceTexts = new Set<string>(); // Track processed source texts to prevent duplicates (speaker mode)
  private processedMessageIds = new Set<string>(); // Track processed message IDs (listener mode)
  private processedMessageTexts = new Map<string, number>(); // Track processed message texts with timestamp to prevent duplicates (listener mode)
  private readonly TEXT_DUPLICATE_WINDOW_MS = 10000; // 10 seconds - ignore same text within this window (listener mode)
  private audioContextUnlocked = false; // Track if audio context has been unlocked (iOS requirement)

  // Duplicate detection for broadcast chunks
  private recentTranscriptions = new Map<string, number>(); // Map of text -> timestamp
  private readonly DUPLICATE_WINDOW_MS = 10000; // 10 seconds - ignore duplicates within this window
  private lastChunkSentTime = 0;
  private readonly MIN_CHUNK_INTERVAL_MS = 2000; // Minimum 2 seconds between chunks

  // Live translation (gpt-realtime-translate) state
  private liveSession?: LiveTranslationSession;
  private recentLiveEnglishTexts: string[] = [];
  liveCaption = '';

  // Conversation context for better translation (store recent source texts)
  private recentBroadcastSourceTexts: string[] = []; // Store recent source texts for conversation context
  private readonly MAX_CONTEXT_CHUNKS = 5; // Use last 5 chunks for context

  private pendingBroadcastHistoryEntries: Array<
    Omit<ConversationHistoryEntry, 'id' | 'timestamp'>
  > = [];
  private pendingListenerHistoryMessages: BroadcastMessage[] = [];
  private savedBroadcastHistoryKeys = new Set<string>();

  // Platform detection
  isAndroid = Capacitor.getPlatform() === 'android';
  isIOS = Capacitor.getPlatform() === 'ios';
  // Detect iOS browser (Safari/Chrome on iPhone) - not just native app
  isIOSBrowser = this.detectIOSBrowser();

  private detectIOSBrowser(): boolean {
    // Check if running in iOS browser (Safari/Chrome on iPhone) - NOT native iOS app
    const platform = Capacitor.getPlatform();

    // Native iOS app should NOT be treated as iOS browser
    if (platform === 'ios') return false; // Native iOS app - use normal behavior

    // Check for iOS browser (web platform with iOS user agent)
    if (platform === 'web') {
      const userAgent =
        navigator.userAgent || navigator.vendor || (window as any).opera;
      return /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
    }

    return false; // Android, PC webapp, etc. - use normal behavior
  }

  isGlobalAdvancedModelEnabled(): boolean {
    return this.advancedModelSettings.isGlobalAdvancedModelEnabled();
  }

  isAdvancedModelEnabled(): boolean {
    return this.advancedModelSettings.getEffectiveUseAdvancedModel(
      this.useAdvancedModel
    );
  }

  onAdvancedModelChange(enabled: boolean): void {
    if (!this.isGlobalAdvancedModelEnabled()) {
      this.useAdvancedModel = enabled;
    }
  }

  private sessionSub?: Subscription;
  private messagesSub?: Subscription;
  private audioChunkSub?: Subscription; // For broadcast audio chunks
  private isListeningSub?: Subscription;
  private recordingSub?: Subscription;
  private translationSub?: Subscription;
  translations: any = {};

  constructor(
    private readonly broadcastService: BroadcastTranslationService,
    private readonly continuousConversation: ContinuousConversationService,
    private readonly conversationHistoryService: ConversationHistoryService,
    private readonly advancedModelSettings: AdvancedModelSettingsService,
    private readonly liveTranslationSettings: LiveTranslationSettingsService,
    private readonly liveTranslation: LiveTranslationService,
    private readonly translationService: TranslationService,
    private readonly translatorService: TranslatorService,
    private readonly authService: AuthService,
    private readonly cdr: ChangeDetectorRef,
    private readonly toastController: ToastController,
    private readonly alertController: AlertController
  ) {
    // Load translations
    this.translations = this.translationService.getAllTranslations();
    this.translationSub = this.translationService.currentLanguage$.subscribe(
      () => {
        this.translations = this.translationService.getAllTranslations();
        this.cdr.markForCheck();
      }
    );

    // Subscribe to recording state
    this.recordingSub = this.continuousConversation.isRecording.subscribe(
      (isRecording) => {
        this.recording = isRecording;
        this.cdr.markForCheck();
      }
    );
  }

  ngOnInit(): void {
    this.loadAvailableLanguages();
    this.selectedSourceLanguage = this.sourceLanguage || 'en';
    this.selectedTargetLanguage = this.targetLanguage || 'en';
    // Don't auto-set listener language - let user choose
    // Only set default if not already set
    if (
      !this.selectedListenerLanguage ||
      this.selectedListenerLanguage === 'en'
    ) {
      this.selectedListenerLanguage = 'en'; // Default, but user can change it
    }
  }

  private loadAvailableLanguages(): void {
    this.availableLanguages = [
      { code: 'en', name: 'English' },
      { code: 'ko', name: 'Korean' },
      { code: 'zh-CN', name: 'Mandarin (Simplified)' },
      { code: 'zh-HK', name: 'Cantonese (Traditional)' },
      { code: 'ru', name: 'Russian' },
      { code: 'ky', name: 'Kyrgyz' },
      { code: 'kk', name: 'Kazakh' },
      { code: 'tg', name: 'Tajik' },
      { code: 'tk', name: 'Turkmen' },
      { code: 'uz', name: 'Uzbek' },
      { code: 'fa', name: 'Farsi' },
      { code: 'es', name: 'Spanish' },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update languages when inputs change
    if (changes['sourceLanguage']) {
      this.selectedSourceLanguage = this.sourceLanguage || 'en';
    }
    if (changes['targetLanguage']) {
      this.selectedTargetLanguage = this.targetLanguage || 'en';
      // Don't auto-reset listener language when targetLanguage changes
      // Listener should be able to choose their own language independently
    }

    // Check if modal is opening or closing
    if (changes['isOpen']) {
      const wasOpen = changes['isOpen'].previousValue;
      const isNowOpen = changes['isOpen'].currentValue;

      if (!wasOpen && isNowOpen) {
        // Modal is opening - initialize
        console.log('[BroadcastTranslationModal] Modal opening');
        // Ensure languages are loaded
        if (this.availableLanguages.length === 0) {
          this.loadAvailableLanguages();
        }
        this.selectedSourceLanguage = this.sourceLanguage || 'en';
        this.selectedTargetLanguage = this.targetLanguage || 'en';
        // Don't reset listener language when modal opens - preserve user's choice
        // Only reset if user hasn't made a choice yet (first time opening)
        if (!this.selectedListenerLanguage) {
          this.selectedListenerLanguage = 'en'; // Default, but user can change it
        }
        this.errorMessage = '';
        this.mode = 'speaker'; // Reset to speaker mode when opening
        this.extraLanguages = []; // Reset extra languages
        this.broadcastCode = ''; // Reset broadcast code
        this.isBroadcasting = false; // Reset broadcasting state
        this.isJoined = false; // Reset joined state
        this.messages = []; // Clear messages
        this.resetBroadcastHistoryBuffer();
        this.availableListenerLanguages = []; // Clear available languages
        this.enteredCode = ''; // Reset entered code
        // Ensure translations are loaded
        this.translations = this.translationService.getAllTranslations();
        // Force change detection after a brief delay to ensure DOM is ready
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 0);
      } else if (wasOpen && !isNowOpen) {
        // Modal is closing - cleanup
        console.log('[BroadcastTranslationModal] Modal closing');
        this.stopBroadcasting();
        this.leaveSession();
        this.stopAllAudio();
      }
    }
  }

  ngOnDestroy(): void {
    this.stopBroadcasting();
    this.leaveSession();
    this.stopAllAudio();
    this.sessionSub?.unsubscribe();
    this.messagesSub?.unsubscribe();
    this.isListeningSub?.unsubscribe();
    this.recordingSub?.unsubscribe();
    this.translationSub?.unsubscribe();
  }

  /**
   * SPEAKER MODE
   */
  async startBroadcasting(): Promise<void> {
    try {
      this.errorMessage = '';

      // Clear conversation context when starting a new broadcast session
      this.recentBroadcastSourceTexts = [];

      // Validate languages
      const allTargetLanguages = [
        this.selectedTargetLanguage,
        ...this.extraLanguages,
      ].filter(Boolean);
      if (allTargetLanguages.length === 0) {
        this.errorMessage = 'Please select at least one target language';
        return;
      }

      // Log target languages for debugging
      console.log(
        `[Broadcast] 🎯 Starting broadcast with ${allTargetLanguages.length} target languages:`,
        allTargetLanguages
      );
      console.log(`[Broadcast] 📋 Language breakdown:`, {
        default: this.selectedTargetLanguage,
        extra: this.extraLanguages,
        all: allTargetLanguages,
      });

      // Create broadcast session
      console.log('[Broadcast] 🔄 Calling createBroadcastSession...');
      let code: string;
      try {
        code = await this.broadcastService.createBroadcastSession(
          this.selectedSourceLanguage,
          allTargetLanguages
        );
        console.log(
          `[Broadcast] ✅ Broadcast session created with code: ${code}`
        );
      } catch (err) {
        console.error('[Broadcast] ❌ Error creating broadcast session:', err);
        this.errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to create broadcast session';
        this.cdr.markForCheck();
        return;
      }

      this.broadcastCode = code;
      this.isBroadcasting = true;
      this.resetBroadcastHistoryBuffer();
      console.log('[Broadcast] 📺 Setting broadcastCode:', code);
      console.log('[Broadcast] 📺 Setting isBroadcasting:', true);
      this.cdr.markForCheck();

      if (this.usesLiveTranslation()) {
        await this.startLiveBroadcast(code, allTargetLanguages);
        return;
      }

      // Start recording (reuse continuous conversation service)
      console.log('[Broadcast] 🎤 Starting recording...');
      await this.continuousConversation.startRecording(
        this.selectedSourceLanguage,
        this.selectedTargetLanguage,
        this.conversationTopic, // Pass conversation topic for better context
        this.lectureMode && !this.isLiveTranslationEnabled(),
        this.useAdvancedModel
      );
      console.log('[Broadcast] ✅ Recording started');

      // Subscribe to audio chunks and send to backend for transcription + translation
      // Backend will: 1) Transcribe audio once, 2) Translate to all languages, 3) Save all to Firestore
      const audioChunkSub =
        this.continuousConversation.audioChunkEvents.subscribe(
          async (chunkData) => {
            if (!chunkData || !this.isBroadcasting) {
              return;
            }

            const { blob: audioBlob, chunkIndex } = chunkData;

            // Rate limiting: Don't send chunks too frequently
            const now = Date.now();
            const timeSinceLastChunk = now - this.lastChunkSentTime;
            if (timeSinceLastChunk < this.MIN_CHUNK_INTERVAL_MS) {
              console.log(
                `[Broadcast] ⏸️ Chunk ${chunkIndex} rate-limited (${timeSinceLastChunk}ms since last chunk, minimum ${this.MIN_CHUNK_INTERVAL_MS}ms)`
              );
              return;
            }

            console.log(
              `[Broadcast] 📡 Received audio chunk ${chunkIndex}, size: ${audioBlob.size} bytes`
            );
            console.log(
              `[Broadcast] 🎯 Sending to backend for transcription + translation to ${allTargetLanguages.length} languages`
            );

            try {
              const user = await this.authService.getCurrentUser();
              if (!user || !user.userId || !user.email) {
                throw new Error('User not authenticated');
              }

              // Collect conversation context from past chunks (last 5 chunks' source text)
              // This helps improve translation quality by understanding the conversation flow
              const conversationContext = this.recentBroadcastSourceTexts
                .slice(-this.MAX_CONTEXT_CHUNKS) // Get last 5 chunks
                .filter((text) => text && text.trim().length > 0); // Filter empty texts

              console.log('[Broadcast] 📚 Collecting conversation context:', {
                totalChunks: this.recentBroadcastSourceTexts.length,
                contextChunks: conversationContext.length,
                contextPreview: conversationContext.map((t) =>
                  t.substring(0, 30)
                ),
              });

              // Send audio chunk to backend - backend handles transcription + translation + saving
              const result = await this.translatorService.broadcastTranscribe(
                audioBlob,
                code,
                this.selectedSourceLanguage,
                allTargetLanguages,
                user.userId,
                user.email,
                this.conversationTopic, // Pass conversation topic for better translation context
                conversationContext, // Pass conversation context for better translation
                this.useAdvancedModel
              );

              this.applyBroadcastResult(result, chunkIndex, now);
            } catch (err) {
              this.handleBroadcastError(err, chunkIndex);
            }
          }
        );

      // Store subscription for cleanup
      this.audioChunkSub = audioChunkSub;

      this.cdr.markForCheck();
    } catch (err) {
      console.error('[Broadcast] Error starting broadcast:', err);
      this.errorMessage =
        err instanceof Error ? err.message : 'Failed to start broadcast';
      this.cdr.markForCheck();
    }
  }

  isLiveTranslationEnabled(): boolean {
    return this.liveTranslationSettings.isLiveTranslationEnabled();
  }

  isLiveSourceSupported(languageCode: string): boolean {
    return this.liveTranslationSettings.isSupportedSourceLanguage(languageCode);
  }

  usesLiveTranslation(): boolean {
    return (
      this.isLiveTranslationEnabled() &&
      this.isLiveSourceSupported(this.selectedSourceLanguage)
    );
  }

  /** Shown when live translation is on but the speaker's language can't be used live. */
  getLiveFallbackNotice(): string {
    return this.translationService.formatMessage(
      'liveTranslationFallback',
      this.getLanguageName(this.selectedSourceLanguage),
      this.getLanguageName('en')
    );
  }

  /**
   * Live broadcast: the speaker's speech is live-translated to English (the
   * pivot language), and each finished English sentence is translated by the
   * backend's advanced model into every target language, then delivered to
   * listeners as usual.
   */
  private async startLiveBroadcast(
    code: string,
    targetLanguages: string[]
  ): Promise<void> {
    const sourceLanguage = this.selectedSourceLanguage;
    this.recentLiveEnglishTexts = [];
    try {
      this.liveSession = await this.liveTranslation.start({
        sourceLanguage,
        targetLanguage: 'en',
        playTranslatedAudio: false,
        segmentOn: sourceLanguage === 'en' ? 'input' : 'output',
        onPartial: (text) => {
          this.liveCaption = text;
          this.cdr.markForCheck();
        },
        onSegment: (segment) =>
          this.sendLiveBroadcastSegment(code, targetLanguages, segment),
        onError: (error, fatal) => {
          console.error('[Broadcast] Live translation error:', error);
          if (fatal) {
            this.stopBroadcasting().then(() => {
              this.errorMessage = error.message;
              this.cdr.markForCheck();
            });
          }
        },
      });
      this.recording = true;
      this.cdr.markForCheck();
    } catch (err) {
      console.error('[Broadcast] Failed to start live translation:', err);
      await this.stopBroadcasting();
      this.errorMessage =
        err instanceof Error ? err.message : 'Failed to start broadcast';
      this.cdr.markForCheck();
    }
  }

  private async sendLiveBroadcastSegment(
    code: string,
    targetLanguages: string[],
    segment: LiveTranslationSegment
  ): Promise<void> {
    if (this.broadcastCode !== code) {
      return;
    }
    const now = Date.now();
    const conversationContext = this.recentLiveEnglishTexts.slice(
      -this.MAX_CONTEXT_CHUNKS
    );
    this.recentLiveEnglishTexts.push(segment.translatedText);
    if (this.recentLiveEnglishTexts.length > this.MAX_CONTEXT_CHUNKS * 2) {
      this.recentLiveEnglishTexts.shift();
    }

    try {
      const result = await this.translatorService.broadcastTranslateText(
        code,
        this.selectedSourceLanguage,
        segment.sourceText,
        segment.translatedText,
        targetLanguages,
        this.conversationTopic,
        conversationContext
      );
      if (this.broadcastCode === code) {
        this.applyBroadcastResult(result, 'live', now);
        this.cdr.markForCheck();
      }
    } catch (err) {
      this.handleBroadcastError(err, 'live');
      if (err instanceof Error && this.isWordLimitError(err.message)) {
        await this.stopBroadcasting();
        this.errorMessage = err.message;
        this.cdr.markForCheck();
      }
    }
  }

  /** Shared handling of a processed broadcast sentence (recorded chunk or live segment). */
  private applyBroadcastResult(
    result: {
      success: boolean;
      skipped?: boolean;
      sourceText: string;
      translations: Array<{ targetLanguage: string; translatedText: string }>;
    },
    chunkIndex: number | string,
    now: number
  ): void {
    if (result && result.success) {
      // Check if chunk was skipped (no text transcribed - likely silence)
      if (result.skipped) {
        console.log(
          `[Broadcast] ⏭️ Chunk ${chunkIndex} skipped (no text transcribed - likely silence or too short)`
        );
        // Don't show error for skipped chunks - this is normal
        return;
      }

      // Duplicate detection: Check if we've seen this exact text recently
      const sourceText = result.sourceText?.trim() || '';
      if (sourceText) {
        const textKey = sourceText.toLowerCase();
        const lastSeenTime = this.recentTranscriptions.get(textKey);

        if (
          lastSeenTime &&
          now - lastSeenTime < this.DUPLICATE_WINDOW_MS
        ) {
          console.log(
            `[Broadcast] 🚫 Chunk ${chunkIndex} duplicate detected (seen ${Math.round(
              (now - lastSeenTime) / 1000
            )}s ago): "${sourceText.substring(0, 50)}..."`
          );
          return; // Skip duplicate
        }

        // Update recent transcriptions map (clean old entries)
        this.recentTranscriptions.set(textKey, now);
        this.cleanOldTranscriptions(now);

        // Store source text for conversation context (for better translation of future chunks)
        this.recentBroadcastSourceTexts.push(sourceText);
        // Keep only last MAX_CONTEXT_CHUNKS * 2 to prevent memory growth
        if (
          this.recentBroadcastSourceTexts.length >
          this.MAX_CONTEXT_CHUNKS * 2
        ) {
          this.recentBroadcastSourceTexts.shift();
        }

        // Store source text for speaker to verify (display on speaker page)
        this.speakerSourceTexts.push({
          text: sourceText,
          timestamp: new Date(),
        });
        // Keep only last 50 transcriptions to prevent memory growth
        if (this.speakerSourceTexts.length > 50) {
          this.speakerSourceTexts.shift();
        }

        // Process for audio if audio is enabled
        if (this.audioEnabled()) {
          this.processSourceTextForAudio(sourceText);
        }

        // Update last chunk sent time
        this.lastChunkSentTime = now;
      }

      this.bufferBroadcastTranslationsForHistory(
        result.sourceText,
        result.translations
      );

      // Chunk was successfully processed
      console.log(
        `[Broadcast] ✅ Backend processed chunk ${chunkIndex}:`
      );
      console.log(
        `  📝 Source text: "${result.sourceText.substring(0, 50)}..."`
      );
      console.log(
        `  🌐 Translations: ${result.translations.length} languages saved`
      );
      result.translations.forEach((t: any) => {
        console.log(
          `    - ${t.targetLanguage}: "${t.translatedText.substring(
            0,
            30
          )}..."`
        );
      });
    } else {
      console.warn(
        `[Broadcast] ⚠️ Backend returned success=false for chunk ${chunkIndex}`
      );
    }
  }

  private handleBroadcastError(err: unknown, chunkIndex: number | string): void {
    console.error(
      `[Broadcast] ❌ Error sending chunk ${chunkIndex} to backend:`,
      err
    );
    const errorMsg =
      err instanceof Error
        ? err.message
        : 'Failed to process audio chunk';

    // Check if it's a word limit error - only show those to users
    if (this.isWordLimitError(errorMsg)) {
      // Show word limit error to user
      this.errorMessage = errorMsg;
      this.showToast(errorMsg, 'warning', 5000);
      // Also emit event to show buy words modal
      this.showBuyWords.emit();
    } else {
      // Log other errors but don't display to user
      console.error(
        '[Broadcast] 💥 Transcription error (logging only, not displaying to user):',
        {
          message: errorMsg,
          chunkIndex,
          error: err,
        }
      );
      // Don't set errorMessage or show toast for non-word-limit errors
    }

    this.cdr.markForCheck();
  }

  async stopBroadcasting(): Promise<void> {
    console.log('[Broadcast] 🛑 Stopping broadcast...');

    // Immediately reset UI state so button becomes responsive
    const codeToDelete = this.broadcastCode;
    this.isBroadcasting = false;
    this.broadcastCode = '';
    this.cdr.markForCheck();

    // Unsubscribe from audio chunks
    this.audioChunkSub?.unsubscribe();
    this.audioChunkSub = undefined;

    if (this.liveSession) {
      const liveSession = this.liveSession;
      this.liveSession = undefined;
      this.liveCaption = '';
      this.recording = false;
      liveSession.stop();
    }

    this.continuousConversation.stopRecording();

    this.saveBufferedBroadcastHistory();

    // Clear conversation context when stopping broadcast
    this.recentBroadcastSourceTexts = [];
    this.recentTranscriptions.clear();

    // Reset all broadcast state
    this.errorMessage = '';
    this.messages = [];
    this.speakerSourceTexts = [];
    this.stopAllAudio();
    this.processedSourceTexts.clear();

    // Delete from Firestore with timeout (don't block UI)
    if (codeToDelete) {
      try {
        console.log('[Broadcast] 🗑️ Deleting broadcast session:', codeToDelete);
        // Add timeout to prevent hanging
        const deletePromise =
          this.broadcastService.endBroadcastSession(codeToDelete);
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Delete timeout')), 10000);
        });

        await Promise.race([deletePromise, timeoutPromise]);
        console.log('[Broadcast] ✅ Broadcast session deleted successfully');
      } catch (err) {
        console.error(
          '[Broadcast] ⚠️ Error ending broadcast session (non-blocking):',
          err
        );
        // Don't block - state is already reset
        // Firestore cleanup can happen in background or retry later
      }
    }

    console.log('[Broadcast] ✅ Broadcast stopped and reset');
    this.cdr.markForCheck();
  }

  addExtraLanguage(): void {
    if (this.extraLanguages.length < 3) {
      // Find first available language that's not already selected (excluding source language)
      const allSelected = [
        this.selectedSourceLanguage,
        this.selectedTargetLanguage,
        ...this.extraLanguages,
      ];
      const available = this.availableLanguages.find(
        (lang) => !allSelected.includes(lang.code)
      );
      this.extraLanguages.push(available?.code || 'en');
      this.cdr.markForCheck();
    }
  }

  removeExtraLanguage(index: number): void {
    console.log('[BroadcastModal] Removing extra language at index:', index);
    if (index >= 0 && index < this.extraLanguages.length) {
      this.extraLanguages.splice(index, 1);
      console.log(
        '[BroadcastModal] Remaining extra languages:',
        this.extraLanguages
      );
      this.cdr.markForCheck();
    } else {
      console.error(
        '[BroadcastModal] Invalid index for removeExtraLanguage:',
        index
      );
    }
  }

  /**
   * Clean old transcriptions from the duplicate detection map
   */
  private cleanOldTranscriptions(now: number): void {
    const cutoff = now - this.DUPLICATE_WINDOW_MS;
    for (const [text, timestamp] of this.recentTranscriptions.entries()) {
      if (timestamp < cutoff) {
        this.recentTranscriptions.delete(text);
      }
    }
  }

  /**
   * Get available languages for target language selection
   * Excludes the source language (no point translating to the same language)
   */
  getAvailableLanguagesForTarget(): { code: string; name: string }[] {
    return this.availableLanguages.filter(
      (lang) => lang.code !== this.selectedSourceLanguage
    );
  }

  /**
   * Get available languages for extra language selection
   * Excludes the source language, default target language, and already selected extra languages
   * But includes the currently selected language for the dropdown being edited
   */
  getAvailableLanguagesForExtra(
    excludeIndex?: number
  ): { code: string; name: string }[] {
    const allSelectedCodes = [
      this.selectedSourceLanguage,
      this.selectedTargetLanguage,
      ...this.extraLanguages,
    ];
    // If excludeIndex is provided, allow the current language in that dropdown
    if (excludeIndex !== undefined && this.extraLanguages[excludeIndex]) {
      const currentLang = this.extraLanguages[excludeIndex];
      // Remove current language from exclusion list so it appears in its own dropdown
      const filtered = allSelectedCodes.filter((lang) => lang !== currentLang);
      return this.availableLanguages.filter(
        (lang) => !filtered.includes(lang.code)
      );
    }
    // For new dropdowns, exclude all selected languages including source
    return this.availableLanguages.filter(
      (lang) => !allSelectedCodes.includes(lang.code)
    );
  }

  copyCode(): void {
    if (this.broadcastCode) {
      navigator.clipboard.writeText(this.broadcastCode).then(() => {
        // Show toast or feedback
        console.log('Code copied to clipboard');
      });
    }
  }

  /**
   * LISTENER MODE
   */
  async joinSession(): Promise<void> {
    try {
      this.errorMessage = '';

      if (!this.enteredCode || this.enteredCode.length !== 6) {
        this.errorMessage = 'Please enter a valid 6-digit code';
        return;
      }

      const session = await this.broadcastService.joinBroadcastSession(
        this.enteredCode
      );

      // Set available languages for this broadcast session
      // Include source language + all target languages (listeners can choose source OR any target)
      const allAvailableLanguages = [
        session.sourceLanguage,
        ...(session.targetLanguages || []),
      ];
      // Remove duplicates and set available languages
      this.availableListenerLanguages = [...new Set(allAvailableLanguages)];

      // Check if selected language is available (source OR target)
      const isSelectedLanguageAvailable =
        this.selectedListenerLanguage === session.sourceLanguage ||
        (session.targetLanguages || []).includes(this.selectedListenerLanguage);

      if (!isSelectedLanguageAvailable) {
        // If selected language is not available, inform user and use first available
        if (this.availableListenerLanguages.length > 0) {
          const previousChoice = this.selectedListenerLanguage;
          // Prefer source language if available, otherwise use first target language
          this.selectedListenerLanguage =
            session.sourceLanguage ||
            (session.targetLanguages && session.targetLanguages.length > 0
              ? session.targetLanguages[0]
              : 'en');
          console.log(
            `[BroadcastTranslationModal] Selected language "${previousChoice}" not available. Using "${this.selectedListenerLanguage}" instead.`
          );
          // Show a brief message that language was adjusted (but don't block joining)
          // The user can change it after joining if needed
        } else {
          this.errorMessage = 'No languages available in this broadcast';
          return;
        }
      }

      // Start listening for messages in the selected language
      console.log(
        `[BroadcastTranslationModal] Starting to listen for language: ${this.selectedListenerLanguage}`
      );
      this.broadcastService.startListening(
        this.enteredCode,
        this.selectedListenerLanguage
      );

      // Subscribe to messages
      this.messagesSub = this.broadcastService.messages.subscribe(
        async (messages) => {
          if (
            this.isJoined &&
            this.messages.length > 0 &&
            messages.length === 0
          ) {
            console.log(
              '[Broadcast] 📝 Messages cleared while listener is joined; saving received messages before clearing'
            );
            this.savePendingListenerHistory();
          }

          this.messages = messages;
          this.bufferListenerMessagesForHistory(messages);

          // Process new messages for audio if enabled
          // Only process messages that haven't been processed yet AND are recent (within 30 seconds)
          if (this.audioEnabled()) {
            const now = Date.now();
            const recentThreshold = 30000; // 30 seconds
            const newMessages = messages.filter((msg) => {
              const messageId =
                msg.id || `${msg.broadcastCode}-${msg.timestamp.getTime()}`;
              const messageAge = now - msg.timestamp.getTime();
              // Only process if not already processed AND message is recent
              return (
                !this.processedMessageIds.has(messageId) &&
                messageAge <= recentThreshold
              );
            });

            if (newMessages.length > 0) {
              console.log(
                `[Broadcast] 🔊 Processing ${newMessages.length} new recent messages for audio`
              );

              if (this.isIOS) {
                // For iOS: Queue the audio, then start playing if queue was empty
                // If audio was already playing, new items will play automatically via onended callback
                const queueWasEmpty =
                  this.audioQueue.length === 0 && !this.isPlayingAudio;
                console.log(
                  '[Broadcast] 🔊 [iOS] Queue state before processing:',
                  {
                    queueWasEmpty,
                    currentQueueSize: this.audioQueue.length,
                    isPlaying: this.isPlayingAudio,
                  }
                );

                await this.processNewMessagesForAudio(newMessages);

                // If queue was empty and we just added items, start playing
                // This is safe because user already toggled audio ON (user interaction context)
                if (
                  queueWasEmpty &&
                  this.audioQueue.length > 0 &&
                  !this.isPlayingAudio
                ) {
                  console.log(
                    '[Broadcast] 🔊 [iOS] Starting playback from newly queued audio'
                  );
                  this.playNextAudio().catch((err) => {
                    console.error(
                      '[Broadcast] 🔊 [iOS] Error starting playback from new messages:',
                      err
                    );
                    this.isPlayingAudio = false;
                  });
                } else {
                  console.log('[Broadcast] 🔊 [iOS] Not starting playback:', {
                    queueWasEmpty,
                    queueSize: this.audioQueue.length,
                    isPlaying: this.isPlayingAudio,
                    reason: queueWasEmpty
                      ? 'queue still empty after processing'
                      : 'already playing or queue had items',
                  });
                }
              } else {
                // For Android/PC: Process and play immediately
                this.processNewMessagesForAudio(newMessages);
              }
            }
          }

          this.cdr.markForCheck();
        }
      );

      this.isJoined = true;
      this.cdr.markForCheck();
    } catch (err) {
      console.error('[Broadcast] ❌ Error joining session:', err);
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to join broadcast';
      this.errorMessage = errorMsg;
      // Reset joined state on error
      this.isJoined = false;
      this.availableListenerLanguages = [];
      this.cdr.markForCheck();
    }
  }

  onListenerLanguageChange(): void {
    if (this.isJoined && this.enteredCode && this.selectedListenerLanguage) {
      console.log(
        `[BroadcastTranslationModal] Listener changed language to: ${this.selectedListenerLanguage}`
      );
      this.savePendingListenerHistory();
      // Stop current listening and restart with new language
      this.broadcastService.stopListening();
      this.processedMessageIds.clear(); // Clear processed messages to re-evaluate audio for new language
      this.stopAllAudio(); // Stop any ongoing audio
      // Start listening again with the new target language
      this.broadcastService.startListening(
        this.enteredCode,
        this.selectedListenerLanguage
      );
    }
  }

  leaveSession(): void {
    this.savePendingListenerHistory();
    this.broadcastService.stopListening();
    this.broadcastService.clear();
    this.isJoined = false;
    this.enteredCode = '';
    this.messages = [];
    this.availableListenerLanguages = [];
    this.resetBroadcastHistoryBuffer();
    this.stopAllAudio();
    this.cdr.markForCheck();
  }

  /**
   * Get available languages for listener selection
   * Only shows languages that are available in the current broadcast session
   */
  getAvailableLanguagesForListener(): { code: string; name: string }[] {
    if (this.isJoined && this.availableListenerLanguages.length > 0) {
      // Filter available languages to only show those in the broadcast session
      return this.availableLanguages.filter((lang) =>
        this.availableListenerLanguages.includes(lang.code)
      );
    }
    // Before joining, show all languages (will be filtered after joining)
    return this.availableLanguages;
  }

  /**
   * Get formatted list of available languages for display
   */
  getAvailableLanguagesDisplay(): string {
    if (!this.isJoined || this.availableListenerLanguages.length === 0) {
      return '';
    }
    return this.availableListenerLanguages
      .map((l) => this.getLanguageName(l))
      .join(', ');
  }

  /**
   * AUDIO - Handles both speaker (source texts) and listener (messages) modes
   */
  toggleAudio(): void {
    const newValue = !this.audioEnabled();
    console.log('[Broadcast] 🔊 toggleAudio called:', {
      newValue,
      mode: this.mode,
      isIOS: this.isIOS,
      isIOSBrowser: this.isIOSBrowser,
      currentQueueSize: this.audioQueue.length,
      isPlaying: this.isPlayingAudio,
      messagesCount: this.messages.length,
      sourceTextsCount: this.speakerSourceTexts.length,
    });

    this.audioEnabled.set(newValue);

    if (!newValue) {
      console.log('[Broadcast] 🔊 Audio disabled - stopping all audio');
      this.stopAllAudio();
      this.audioContextUnlocked = false; // Reset when audio is disabled
    } else {
      if (this.mode === 'speaker') {
        // Speaker mode: Process source texts for audio
        console.log('[Broadcast] 🔊 Audio enabled - processing source texts');
        if (this.isIOSBrowser) {
          // For iOS: Start playback if queue has items
          if (this.audioQueue.length > 0 && !this.isPlayingAudio) {
            Promise.resolve().then(() => {
              if (
                !this.isPlayingAudio &&
                this.audioQueue.length > 0 &&
                this.audioEnabled()
              ) {
                this.playNextAudio().catch((err) => {
                  console.warn('[Broadcast] Failed to play queued audio:', err);
                });
              }
            });
          }
          // Process existing source texts for audio
          this.processExistingSourceTextsForAudio();
        } else {
          // For non-iOS: Process existing source texts
          this.processExistingSourceTextsForAudio();
        }
      } else {
        // Listener mode: Process messages for audio
        console.log('[Broadcast] 🔊 Audio enabled - processing messages');
        // For iOS browser: User interaction context is active NOW (button click)
        // Process messages immediately - audio will play when ready
        // User interaction context from button click should still be valid
        if (this.isIOSBrowser) {
          console.log(
            '[Broadcast] 🔊 [iOS Browser] Processing messages (user interaction context should be valid)'
          );
          this.processExistingMessagesForAudio();
        } else if (this.isIOS) {
          console.log(
            '[Broadcast] 🔊 [iOS Native] Using iOS-specific queue approach'
          );
          this.processExistingMessagesForAudioIOS();
        } else {
          console.log('[Broadcast] 🔊 [Android/PC] Using standard approach');
          // For Android/PC: Process and play immediately (current working approach)
          this.processExistingMessagesForAudio();
        }
      }
    }

    this.cdr.markForCheck();
  }

  /**
   * iOS-specific: Process messages and queue all audio, then start playing
   * This ensures the first play() happens in user interaction context
   */
  private async processExistingMessagesForAudioIOS(): Promise<void> {
    console.log(
      '[Broadcast] 🔊 [iOS] processExistingMessagesForAudioIOS started'
    );

    // Only process recent messages (within last 30 seconds)
    const recentThreshold = 30000; // 30 seconds in milliseconds
    const now = Date.now();
    const recentMessages = this.messages.filter((msg) => {
      const messageAge = now - msg.timestamp.getTime();
      return messageAge <= recentThreshold;
    });

    console.log('[Broadcast] 🔊 [iOS] Audio enabled. Processing messages:', {
      recentMessagesCount: recentMessages.length,
      totalMessagesCount: this.messages.length,
      currentQueueSize: this.audioQueue.length,
      isPlaying: this.isPlayingAudio,
    });

    // First, generate and queue all audio
    console.log(
      '[Broadcast] 🔊 [iOS] Starting to process messages for audio generation...'
    );
    await this.processNewMessagesForAudio(recentMessages);

    console.log(
      '[Broadcast] 🔊 [iOS] Finished processing messages. Queue state:',
      {
        queueSize: this.audioQueue.length,
        isPlaying: this.isPlayingAudio,
        audioEnabled: this.audioEnabled(),
      }
    );

    // After all audio is queued, start playing from the queue
    // This first play() happens in user interaction context (toggleAudio was just called)
    if (this.audioQueue.length > 0 && !this.isPlayingAudio) {
      console.log(
        `[Broadcast] 🔊 [iOS] Starting playback from queue (${this.audioQueue.length} items)`
      );
      this.playNextAudio().catch((err) => {
        console.error('[Broadcast] 🔊 [iOS] Error starting playback:', err);
        console.error('[Broadcast] 🔊 [iOS] Error details:', {
          message: err.message,
          stack: err.stack,
          name: err.name,
        });
        this.isPlayingAudio = false;
      });
    } else {
      console.log('[Broadcast] 🔊 [iOS] Not starting playback:', {
        queueSize: this.audioQueue.length,
        isPlaying: this.isPlayingAudio,
        reason:
          this.audioQueue.length === 0 ? 'queue empty' : 'already playing',
      });
    }
  }

  /**
   * Process existing messages for audio playback (Android/PC approach)
   */
  private processExistingMessagesForAudio(): void {
    // Only process recent messages (within last 30 seconds) to avoid playing old messages
    const recentThreshold = 30000; // 30 seconds in milliseconds
    const now = Date.now();
    const recentMessages = this.messages.filter((msg) => {
      const messageAge = now - msg.timestamp.getTime();
      return messageAge <= recentThreshold;
    });

    console.log(
      `[Broadcast] 🔊 Audio enabled. Processing ${recentMessages.length} recent messages (out of ${this.messages.length} total)`
    );
    this.processNewMessagesForAudio(recentMessages);
  }

  /**
   * Process messages for audio playback
   * Only processes messages that haven't been processed yet
   */
  private async processNewMessagesForAudio(
    messages: BroadcastMessage[]
  ): Promise<void> {
    console.log('[Broadcast] 🔊 processNewMessagesForAudio called:', {
      messagesCount: messages.length,
      isIOS: this.isIOS,
      currentQueueSize: this.audioQueue.length,
      isPlaying: this.isPlayingAudio,
    });

    const now = Date.now();
    const recentThreshold = 6000; // Only process messages from last 6 seconds

    let processedCount = 0;
    let skippedCount = 0;
    let errorCount = 0;

    for (const message of messages) {
      // Skip if already processed by message ID
      const messageId =
        message.id || `${message.broadcastCode}-${message.timestamp.getTime()}`;
      if (this.processedMessageIds.has(messageId)) {
        skippedCount++;
        console.log(
          '[Broadcast] 🔊 Skipping already processed message (by ID):',
          messageId
        );
        continue;
      }

      // Skip if message is too old (only process recent messages)
      const messageAge = now - message.timestamp.getTime();
      if (messageAge > recentThreshold) {
        console.log(
          `[Broadcast] 🔊 Skipping old message (${Math.round(
            messageAge / 1000
          )}s old): "${message.translatedText.substring(0, 30)}..."`
        );
        // Mark as processed so we don't try again
        this.processedMessageIds.add(messageId);
        skippedCount++;
        continue;
      }

      // Check if same text was recently processed (prevent duplicate audio for repeated speech)
      const messageText = message.translatedText.trim().toLowerCase();

      // First check exact match
      const lastProcessedTime = this.processedMessageTexts.get(messageText);
      if (
        lastProcessedTime &&
        now - lastProcessedTime < this.TEXT_DUPLICATE_WINDOW_MS
      ) {
        const timeSince = Math.round((now - lastProcessedTime) / 1000);
        console.log(
          `[Broadcast] 🔊 Skipping duplicate text (exact match, processed ${timeSince}s ago): "${message.translatedText.substring(
            0,
            50
          )}..."`
        );
        // Mark message ID as processed but don't update text timestamp (keep original)
        this.processedMessageIds.add(messageId);
        skippedCount++;
        continue;
      }

      // Check for similar texts (fuzzy matching) - catches minor variations like "Yong" vs "Young"
      const similarText = this.findSimilarProcessedText(messageText, now);
      if (similarText) {
        const timeSince = Math.round((now - similarText.timestamp) / 1000);
        const similarity = this.calculateTextSimilarity(
          messageText,
          similarText.text
        );
        console.warn(
          `[Broadcast] 🔊 Skipping similar text (${Math.round(
            similarity * 100
          )}% similar, processed ${timeSince}s ago): "${message.translatedText.substring(
            0,
            50
          )}..." vs "${similarText.text.substring(0, 50)}..."`
        );
        // Mark message ID as processed
        this.processedMessageIds.add(messageId);
        skippedCount++;
        continue;
      }

      // Also check if this text is already in the queue (before generating audio)
      const alreadyInQueue = this.audioQueue.some((item) => {
        const itemNormalized = item.text.trim().toLowerCase();
        // Check both exact match and similarity
        if (itemNormalized === messageText) return true;
        const similarity = this.calculateTextSimilarity(
          messageText,
          itemNormalized
        );
        return similarity > 0.9; // 90% similar
      });
      if (alreadyInQueue) {
        console.log(
          `[Broadcast] 🔊 Skipping duplicate text (already in queue): "${message.translatedText.substring(
            0,
            50
          )}..."`
        );
        // Mark message ID as processed
        this.processedMessageIds.add(messageId);
        skippedCount++;
        continue;
      }

      // Mark message ID and text as processed IMMEDIATELY to prevent duplicate processing
      // This prevents race conditions where multiple messages with same text start TTS simultaneously
      this.processedMessageIds.add(messageId);
      const normalizedText = message.translatedText.trim().toLowerCase();
      this.processedMessageTexts.set(normalizedText, now);

      // Clean up old text entries to prevent memory leak
      this.cleanOldProcessedTexts(now);

      try {
        console.log(
          `[Broadcast] 🔊 [${
            this.isIOS ? 'iOS' : 'Other'
          }] Generating audio for message:`,
          {
            messageId,
            age: `${Math.round(messageAge / 1000)}s`,
            text: message.translatedText.substring(0, 50),
            targetLanguage: message.targetLanguage,
          }
        );

        const audioResult = await this.translatorService.tts(
          message.translatedText,
          1.0,
          'alloy',
          message.targetLanguage
        );

        if (audioResult && audioResult.audioUrl) {
          // Double-check: Make sure this message hasn't been queued already
          // This prevents duplicate audio in the queue
          // Use normalized text comparison (like continuous conversation)

          // Check if already in queue
          const alreadyQueued = this.audioQueue.some((item) => {
            const itemNormalized = item.text.trim().toLowerCase();
            return itemNormalized === normalizedText;
          });

          if (alreadyQueued) {
            console.warn(
              '[Broadcast] 🔊 Skipping duplicate audio (already in queue):',
              {
                messageId,
                text: message.translatedText.substring(0, 50),
                queueSize: this.audioQueue.length,
              }
            );
            // Text already marked as processed above, so we can just continue
            continue;
          }

          console.log('[Broadcast] 🔊 TTS result received:', {
            hasAudioUrl: !!audioResult.audioUrl,
            audioUrlType: typeof audioResult.audioUrl,
            audioUrlLength: audioResult.audioUrl?.length,
            queueSizeBefore: this.audioQueue.length,
          });

          this.audioQueue.push({
            audioUrl: audioResult.audioUrl,
            text: message.translatedText,
          });

          // Text was already marked as processed before TTS to prevent race conditions
          // Update timestamp to reflect when it was actually added to queue
          this.processedMessageTexts.set(normalizedText, Date.now());

          processedCount++;
          console.log('[Broadcast] 🔊 Audio added to queue:', {
            queueSizeAfter: this.audioQueue.length,
            isIOS: this.isIOS,
            isPlaying: this.isPlayingAudio,
          });

          // Try to start playback AFTER adding to queue
          // For iOS browser: Play immediately without delays to maintain user interaction context
          // For other platforms: Use small delay to prevent race conditions
          if (!this.isPlayingAudio && this.audioQueue.length > 0) {
            if (this.isIOSBrowser) {
              // For iOS browser: Play immediately - call directly in async chain, no delays
              console.log(
                '[Broadcast] 🔊 [iOS Browser] Audio added to queue, starting playback immediately'
              );
              // Call directly - we're still in the async chain from TTS generation
              // This maintains the user interaction context as much as possible
              this.playNextAudio().catch((err) => {
                console.warn(
                  '[Broadcast] 🔊 [iOS Browser] Error in playNextAudio:',
                  err
                );
                this.isPlayingAudio = false;
                // Try again if there are items in queue
                if (this.audioQueue.length > 0) {
                  this.playNextAudio().catch(console.error);
                }
              });
            } else {
              // For all other platforms: Start playback with a small delay to prevent race conditions
              const delay = this.isIOS ? 100 : 300;
              setTimeout(() => {
                if (
                  !this.isPlayingAudio &&
                  this.audioQueue.length > 0 &&
                  this.audioEnabled()
                ) {
                  this.playNextAudio().catch((err) => {
                    console.error(
                      '[Broadcast] 🔊 Error in playNextAudio:',
                      err
                    );
                    // Reset flag on error so queue can continue
                    this.isPlayingAudio = false;
                    // Try again if there are items in queue
                    if (this.audioQueue.length > 0) {
                      this.playNextAudio().catch(console.error);
                    }
                  });
                }
              }, delay);
            }
          } else {
            console.log('[Broadcast] 🔊 Not starting playback:', {
              isPlaying: this.isPlayingAudio,
              queueSize: this.audioQueue.length,
              reason: this.isPlayingAudio ? 'already playing' : 'queue empty',
            });
          }
        } else {
          console.warn('[Broadcast] 🔊 TTS result missing audioUrl:', {
            hasResult: !!audioResult,
            resultKeys: audioResult ? Object.keys(audioResult) : [],
          });
        }
      } catch (error) {
        errorCount++;
        console.error('[Broadcast] 🔊 Error generating audio:', {
          error,
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
      }
    }

    console.log('[Broadcast] 🔊 processNewMessagesForAudio completed:', {
      processed: processedCount,
      skipped: skippedCount,
      errors: errorCount,
      finalQueueSize: this.audioQueue.length,
    });
  }

  private async playNextAudio(): Promise<void> {
    console.log('[Broadcast] 🔊 playNextAudio called:', {
      queueSize: this.audioQueue.length,
      audioEnabled: this.audioEnabled(),
      isPlaying: this.isPlayingAudio,
      isIOS: this.isIOS,
      hasCurrentAudio: !!this.currentAudio,
    });

    if (this.audioQueue.length === 0 || !this.audioEnabled()) {
      console.log('[Broadcast] 🔊 playNextAudio: Exiting early:', {
        queueSize: this.audioQueue.length,
        audioEnabled: this.audioEnabled(),
        reason: this.audioQueue.length === 0 ? 'queue empty' : 'audio disabled',
      });
      this.isPlayingAudio = false;
      return;
    }

    if (this.isPlayingAudio) {
      console.log('[Broadcast] 🔊 playNextAudio: Already playing, skipping');
      return;
    }

    this.isPlayingAudio = true;
    const audioItem = this.audioQueue.shift();

    if (!audioItem) {
      console.warn('[Broadcast] 🔊 playNextAudio: No audio item after shift');
      this.isPlayingAudio = false;
      return;
    }

    // Store the text we're about to play to prevent replaying the same audio
    const playingText = audioItem.text;
    console.log('[Broadcast] 🔊 playNextAudio: Starting playback:', {
      audioUrl: audioItem.audioUrl.substring(0, 50) + '...',
      text: playingText.substring(0, 50),
      queueRemaining: this.audioQueue.length,
      isIOS: this.isIOS,
    });

    try {
      // Stop any currently playing audio
      if (this.currentAudio) {
        console.log('[Broadcast] 🔊 Stopping previous audio element');
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
        this.currentAudio.onended = null;
        this.currentAudio.onerror = null;
      }

      // Create new audio element
      console.log('[Broadcast] 🔊 Creating new Audio element');
      this.currentAudio = new Audio(audioItem.audioUrl);
      this.currentAudio.volume = 1.0; // Force max volume

      // iOS-specific: Preload the audio to ensure blob URL is valid
      // Set preload to ensure audio is loaded before playing
      this.currentAudio.preload = 'auto';

      // Keep a reference to the blob URL to prevent garbage collection
      // Store it on the audio element itself
      (this.currentAudio as any)._blobUrl = audioItem.audioUrl;

      console.log('[Broadcast] 🔊 Audio element created:', {
        src: this.currentAudio.src.substring(0, 50) + '...',
        volume: this.currentAudio.volume,
        readyState: this.currentAudio.readyState,
        networkState: this.currentAudio.networkState,
        preload: this.currentAudio.preload,
      });

      // Debug audio metadata
      this.currentAudio.onloadedmetadata = () => {
        console.log(
          '[Broadcast] 🔊 [onloadedmetadata] Audio metadata loaded:',
          {
            duration: this.currentAudio?.duration,
            readyState: this.currentAudio?.readyState,
            networkState: this.currentAudio?.networkState,
          }
        );
      };

      this.currentAudio.oncanplay = () => {
        console.log('[Broadcast] 🔊 [oncanplay] Audio can play:', {
          readyState: this.currentAudio?.readyState,
          networkState: this.currentAudio?.networkState,
        });
      };

      this.currentAudio.oncanplaythrough = () => {
        console.log('[Broadcast] 🔊 [oncanplaythrough] Audio can play through');
      };

      this.currentAudio.onplay = () => {
        console.log('[Broadcast] 🔊 [onplay] Audio started playing');
      };

      this.currentAudio.onplaying = () => {
        console.log('[Broadcast] 🔊 [onplaying] Audio is playing');
      };

      // iOS-specific: Monitor timeupdate to detect if audio is actually progressing
      // If currentTime doesn't change, iOS might be silently failing
      let lastCurrentTime = 0;
      let timeUpdateCount = 0;
      this.currentAudio.ontimeupdate = () => {
        if (this.currentAudio) {
          timeUpdateCount++;
          const currentTime = this.currentAudio.currentTime;
          if (timeUpdateCount <= 5 || timeUpdateCount % 10 === 0) {
            console.log('[Broadcast] 🔊 [ontimeupdate] Audio time update:', {
              currentTime,
              duration: this.currentAudio.duration,
              progress:
                this.currentAudio.duration > 0
                  ? ((currentTime / this.currentAudio.duration) * 100).toFixed(
                      1
                    ) + '%'
                  : 'N/A',
              paused: this.currentAudio.paused,
              ended: this.currentAudio.ended,
            });
          }

          // Detect if audio is stuck (not progressing)
          if (
            currentTime === lastCurrentTime &&
            !this.currentAudio.paused &&
            !this.currentAudio.ended &&
            currentTime > 0
          ) {
            console.warn(
              '[Broadcast] 🔊 [ontimeupdate] Audio appears stuck at:',
              currentTime
            );
          }
          lastCurrentTime = currentTime;
        }
      };

      // Set up event handlers BEFORE playing
      this.currentAudio.onended = () => {
        console.log('[Broadcast] 🔊 [onended] Audio ended:', {
          text: playingText.substring(0, 50),
          queueRemaining: this.audioQueue.length,
          audioEnabled: this.audioEnabled(),
        });
        // Clean up
        URL.revokeObjectURL(audioItem.audioUrl);
        this.currentAudio = undefined;
        this.isPlayingAudio = false;

        // Play next audio in queue ONLY if there are NEW items
        // Don't replay the same audio that just ended
        // Similar to continuous conversation - just play next without checking text
        // The duplicate detection in processNewMessagesForAudio should prevent duplicates from being added
        if (this.audioQueue.length > 0 && this.audioEnabled()) {
          // Use normalized text comparison to catch duplicates
          const playingTextNormalized = playingText.trim().toLowerCase();

          // Remove any duplicates from the front of the queue
          while (this.audioQueue.length > 0) {
            const nextItem = this.audioQueue[0];
            const nextTextNormalized = nextItem.text.trim().toLowerCase();

            if (nextTextNormalized === playingTextNormalized) {
              console.log(
                '[Broadcast] 🔊 [onended] Removing duplicate from queue:',
                {
                  text: nextItem.text.substring(0, 50),
                  queueSize: this.audioQueue.length,
                }
              );
              const removed = this.audioQueue.shift();
              if (removed) {
                URL.revokeObjectURL(removed.audioUrl);
              }
            } else {
              // Found a different item, play it
              break;
            }
          }

          // Play next audio if available
          if (this.audioQueue.length > 0) {
            const nextItem = this.audioQueue[0];
            console.log(
              '[Broadcast] 🔊 [onended] Playing next audio from queue:',
              {
                previousText: playingText.substring(0, 50),
                nextText: nextItem.text.substring(0, 50),
                queueSize: this.audioQueue.length,
              }
            );
            // Small delay before playing next to ensure iOS has fully released the previous audio
            setTimeout(() => {
              this.playNextAudio().catch((err) => {
                console.error(
                  '[Broadcast] 🔊 [onended] Error playing next audio:',
                  {
                    error: err,
                    message: err instanceof Error ? err.message : String(err),
                    stack: err instanceof Error ? err.stack : undefined,
                  }
                );
                this.isPlayingAudio = false;
              });
            }, 50);
          } else {
            console.log(
              '[Broadcast] 🔊 [onended] No more audio to play after removing duplicates:',
              {
                queueSize: this.audioQueue.length,
                audioEnabled: this.audioEnabled(),
              }
            );
            this.isPlayingAudio = false;
          }
        } else {
          console.log(
            '[Broadcast] 🔊 [onended] No more audio to play - stopping:',
            {
              queueSize: this.audioQueue.length,
              audioEnabled: this.audioEnabled(),
            }
          );
          this.isPlayingAudio = false;
        }
      };

      // Handle pause events - iOS sometimes pauses audio unexpectedly
      // This must be set AFTER onended to avoid conflicts
      this.currentAudio.onpause = () => {
        console.log('[Broadcast] 🔊 [onpause] Audio paused:', {
          text: playingText.substring(0, 50),
          paused: this.currentAudio?.paused,
          currentTime: this.currentAudio?.currentTime,
          duration: this.currentAudio?.duration,
          ended: this.currentAudio?.ended,
        });

        // If audio was paused but not ended, and we're still supposed to be playing, try to resume
        // This handles iOS interruptions (like phone calls, notifications, etc.)
        if (
          this.currentAudio &&
          !this.currentAudio.ended &&
          this.audioEnabled() &&
          this.isPlayingAudio
        ) {
          console.log(
            '[Broadcast] 🔊 [onpause] Attempting to resume paused audio'
          );
          // Small delay before resuming to let iOS settle
          setTimeout(() => {
            if (
              this.currentAudio &&
              !this.currentAudio.ended &&
              !this.currentAudio.paused
            ) {
              // Already playing, do nothing
              return;
            }
            if (this.currentAudio && !this.currentAudio.ended) {
              this.currentAudio.play().catch((err) => {
                console.error(
                  '[Broadcast] 🔊 [onpause] Error resuming audio:',
                  err
                );
                // If resume fails, treat as ended and move to next
                // Manually trigger the ended logic instead of calling onended directly
                if (this.currentAudio && this.currentAudio.onended) {
                  // Create a synthetic event to satisfy TypeScript
                  const syntheticEvent = new Event('ended') as any;
                  this.currentAudio.onended(syntheticEvent);
                }
              });
            }
          }, 100);
        }
      };

      // Handle errors
      this.currentAudio.onerror = (error) => {
        console.error('[Broadcast] 🔊 [onerror] Error playing audio:', {
          error,
          errorEvent: error,
          audioElement: {
            error: this.currentAudio?.error,
            networkState: this.currentAudio?.networkState,
            readyState: this.currentAudio?.readyState,
            src: this.currentAudio?.src?.substring(0, 50),
          },
          audioItem: {
            text: audioItem.text.substring(0, 50),
            audioUrl: audioItem.audioUrl.substring(0, 50),
          },
        });
        URL.revokeObjectURL(audioItem.audioUrl);
        this.currentAudio = undefined;
        this.isPlayingAudio = false;

        // Try next audio in queue
        if (this.audioQueue.length > 0 && this.audioEnabled()) {
          console.log('[Broadcast] 🔊 [onerror] Trying next audio in queue');
          this.playNextAudio().catch((err) => {
            console.error(
              '[Broadcast] 🔊 [onerror] Error playing next audio:',
              err
            );
            this.isPlayingAudio = false;
          });
        }
      };

      // Play the audio
      console.log('[Broadcast] 🔊 Calling audio.play()...');

      // For native iOS app: Wait for audio to be ready
      // For iOS browser: Use simpler approach like manual recording (no waiting for canplay)
      if (
        this.isIOS &&
        !this.isIOSBrowser &&
        this.currentAudio &&
        this.currentAudio.readyState < 2
      ) {
        console.log(
          '[Broadcast] 🔊 [iOS Native] Waiting for audio to be ready...',
          {
            readyState: this.currentAudio.readyState,
          }
        );
        await new Promise<void>((resolve) => {
          if (!this.currentAudio) {
            resolve();
            return;
          }
          const onCanPlay = () => {
            this.currentAudio?.removeEventListener('canplay', onCanPlay);
            console.log('[Broadcast] 🔊 [iOS Native] Audio ready to play');
            resolve();
          };
          this.currentAudio.addEventListener('canplay', onCanPlay);
          // Timeout after 5 seconds to prevent hanging
          setTimeout(() => {
            this.currentAudio?.removeEventListener('canplay', onCanPlay);
            console.warn(
              '[Broadcast] 🔊 [iOS Native] Audio ready timeout, proceeding anyway'
            );
            resolve();
          }, 5000);
        });
      }

      // For iOS browser: Use promise-based approach like Android/PC (but simpler error handling)
      // For native iOS: Use promise-based approach
      const playPromise = this.currentAudio.play();
      console.log('[Broadcast] 🔊 audio.play() called, waiting for promise...');

      await playPromise;

      // iOS-specific: Verify audio is actually playing (for both native and browser)
      // Sometimes iOS resolves the promise but doesn't actually play
      if (this.isIOS) {
        await new Promise<void>((resolve) => {
          const checkPlaying = () => {
            if (!this.currentAudio) {
              resolve();
              return;
            }

            // If audio is paused or ended immediately after play(), there's an issue
            if (this.currentAudio.paused && !this.currentAudio.ended) {
              console.warn(
                '[Broadcast] 🔊 [iOS] Audio paused immediately after play(), attempting resume...'
              );
              this.currentAudio.play().catch((err) => {
                console.error('[Broadcast] 🔊 [iOS] Failed to resume:', err);
              });
            }

            // Wait a bit to see if audio actually starts
            setTimeout(() => {
              if (this.currentAudio) {
                console.log('[Broadcast] 🔊 [iOS] Audio playback check:', {
                  paused: this.currentAudio.paused,
                  ended: this.currentAudio.ended,
                  currentTime: this.currentAudio.currentTime,
                  duration: this.currentAudio.duration,
                });
              }
              resolve();
            }, 100);
          };

          // Check immediately and after a short delay
          checkPlaying();
        });
      }

      console.log(
        '[Broadcast] 🔊 audio.play() promise resolved - audio should be playing:',
        {
          paused: this.currentAudio.paused,
          currentTime: this.currentAudio.currentTime,
          duration: this.currentAudio.duration,
          readyState: this.currentAudio.readyState,
          queueRemaining: this.audioQueue.length,
        }
      );
    } catch (error) {
      console.error('[Broadcast] 🔊 Error in playNextAudio catch block:', {
        error,
        message: error instanceof Error ? error.message : String(error),
        name: error instanceof Error ? error.name : undefined,
        stack: error instanceof Error ? error.stack : undefined,
        audioElement: {
          error: this.currentAudio?.error,
          networkState: this.currentAudio?.networkState,
          readyState: this.currentAudio?.readyState,
          paused: this.currentAudio?.paused,
        },
        audioItem: {
          text: audioItem.text.substring(0, 50),
          audioUrl: audioItem.audioUrl.substring(0, 50),
        },
      });
      URL.revokeObjectURL(audioItem.audioUrl);
      this.currentAudio = undefined;
      this.isPlayingAudio = false;

      // Try next audio in queue
      if (this.audioQueue.length > 0 && this.audioEnabled()) {
        console.log('[Broadcast] 🔊 [catch] Trying next audio in queue');
        this.playNextAudio().catch((err) => {
          console.error(
            '[Broadcast] 🔊 [catch] Error playing next audio:',
            err
          );
          this.isPlayingAudio = false;
        });
      }
    }
  }

  /**
   * Clean up old processed text entries to prevent memory leak
   */
  private cleanOldProcessedTexts(now: number): void {
    const cutoff = now - this.TEXT_DUPLICATE_WINDOW_MS;
    for (const [text, timestamp] of this.processedMessageTexts.entries()) {
      if (timestamp < cutoff) {
        this.processedMessageTexts.delete(text);
      }
    }
  }

  /**
   * Calculate text similarity using Levenshtein distance
   * Returns a value between 0 (completely different) and 1 (identical)
   */
  private calculateTextSimilarity(text1: string, text2: string): number {
    if (text1 === text2) return 1.0;
    if (text1.length === 0 || text2.length === 0) return 0.0;

    const maxLength = Math.max(text1.length, text2.length);
    const distance = this.levenshteinDistance(text1, text2);
    return 1 - distance / maxLength;
  }

  /**
   * Calculate Levenshtein distance between two strings
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const matrix: number[][] = [];
    const len1 = str1.length;
    const len2 = str2.length;

    // Initialize matrix
    for (let i = 0; i <= len1; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= len2; j++) {
      matrix[0][j] = j;
    }

    // Fill matrix
    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        if (str1[i - 1] === str2[j - 1]) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j] + 1, // deletion
            matrix[i][j - 1] + 1, // insertion
            matrix[i - 1][j - 1] + 1 // substitution
          );
        }
      }
    }

    return matrix[len1][len2];
  }

  /**
   * Find a similar text in recently processed texts
   * Returns the similar text and its timestamp if found, null otherwise
   */
  private findSimilarProcessedText(
    text: string,
    now: number
  ): { text: string; timestamp: number } | null {
    const cutoff = now - this.TEXT_DUPLICATE_WINDOW_MS;

    for (const [
      processedText,
      timestamp,
    ] of this.processedMessageTexts.entries()) {
      // Skip old entries
      if (timestamp < cutoff) continue;

      // Skip exact matches (already checked)
      if (processedText === text) continue;

      // Check similarity
      const similarity = this.calculateTextSimilarity(text, processedText);

      // If >90% similar, consider it a duplicate
      if (similarity > 0.9) {
        return { text: processedText, timestamp };
      }

      // For short texts, also check if one contains the other (pattern matching)
      if (text.length < 50 && processedText.length < 50) {
        if (text.includes(processedText) || processedText.includes(text)) {
          const shorter =
            text.length < processedText.length ? text : processedText;
          const longer =
            text.length >= processedText.length ? text : processedText;
          const overlapRatio = shorter.length / longer.length;

          // If >85% overlap, consider it a duplicate
          if (overlapRatio > 0.85) {
            return { text: processedText, timestamp };
          }
        }
      }
    }

    return null;
  }

  /**
   * Unlock iOS browser audio context by playing a silent audio
   * This must be called in user interaction context (e.g., button click)
   */
  private async unlockIOSAudioContext(): Promise<void> {
    if (!this.isIOSBrowser) return;

    // Skip unlock attempt - iOS Safari data URLs don't work reliably
    // Instead, we'll rely on the user interaction context from the button click
    // When audio is ready, we'll try to play it directly
    console.log(
      '[Broadcast] 🔊 [iOS Browser] Skipping silent audio unlock (not reliable on iOS Safari)'
    );
    console.log(
      '[Broadcast] 🔊 [iOS Browser] Will attempt to play audio directly when ready'
    );
    this.audioContextUnlocked = true; // Mark as "unlocked" so we at least try
  }

  private stopAllAudio(): void {
    // Prevent any pending playback from starting
    this.isPlayingAudio = false;

    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      // Critical: Remove handlers so they don't trigger next playback or errors
      this.currentAudio.onended = null;
      this.currentAudio.onerror = null;
      this.currentAudio = undefined;
    }
    // Clear queue and revoke URLs
    while (this.audioQueue.length > 0) {
      const item = this.audioQueue.shift();
      if (item) {
        URL.revokeObjectURL(item.audioUrl);
      }
    }
  }

  private resetBroadcastHistoryBuffer(): void {
    this.pendingBroadcastHistoryEntries = [];
    this.pendingListenerHistoryMessages = [];
    this.savedBroadcastHistoryKeys.clear();
  }

  private getBroadcastHistoryKey(
    originalText: string,
    translatedText: string,
    sourceLanguage: string,
    targetLanguage: string
  ): string {
    return [
      sourceLanguage,
      targetLanguage,
      originalText.trim().toLowerCase(),
      translatedText.trim().toLowerCase(),
    ].join('|');
  }

  private bufferBroadcastTranslationsForHistory(
    sourceText: string,
    translations: Array<{ targetLanguage: string; translatedText: string }>
  ): void {
    const originalText = sourceText?.trim();
    if (!originalText || !translations?.length) {
      return;
    }

    for (const translation of translations) {
      const translatedText = translation.translatedText?.trim() || '';
      const targetLanguage =
        translation.targetLanguage || this.selectedTargetLanguage;

      if (!translatedText) {
        continue;
      }

      const historyKey = this.getBroadcastHistoryKey(
        originalText,
        translatedText,
        this.selectedSourceLanguage,
        targetLanguage
      );

      if (this.savedBroadcastHistoryKeys.has(historyKey)) {
        continue;
      }

      this.savedBroadcastHistoryKeys.add(historyKey);
      this.pendingBroadcastHistoryEntries.push({
        originalText,
        translatedText,
        userLanguage: this.selectedSourceLanguage || 'en',
        targetLanguage: targetLanguage || 'en',
        confidence: 0,
        inputType: 'speech',
      });
    }
  }

  private saveBufferedBroadcastHistory(): void {
    if (this.pendingBroadcastHistoryEntries.length === 0) {
      return;
    }

    console.log('[Broadcast] 📝 Saving broadcast speaker history:', {
      entryCount: this.pendingBroadcastHistoryEntries.length,
    });

    for (const entry of this.pendingBroadcastHistoryEntries) {
      this.conversationHistoryService.addConversation(entry);
    }

    this.pendingBroadcastHistoryEntries = [];
  }

  private bufferListenerMessagesForHistory(messages: BroadcastMessage[]): void {
    if (!messages?.length) {
      return;
    }

    for (const message of messages) {
      if (message.targetLanguage !== this.selectedListenerLanguage) {
        continue;
      }

      const messageKey =
        message.id ||
        this.getBroadcastHistoryKey(
          message.sourceText || '',
          message.translatedText || '',
          message.sourceLanguage || this.selectedSourceLanguage,
          message.targetLanguage || this.selectedListenerLanguage
        );

      const alreadyBuffered = this.pendingListenerHistoryMessages.some(
        (bufferedMessage) =>
          (bufferedMessage.id ||
            this.getBroadcastHistoryKey(
              bufferedMessage.sourceText || '',
              bufferedMessage.translatedText || '',
              bufferedMessage.sourceLanguage || this.selectedSourceLanguage,
              bufferedMessage.targetLanguage || this.selectedListenerLanguage
            )) === messageKey
      );

      if (!alreadyBuffered) {
        this.pendingListenerHistoryMessages.push(message);
      }
    }
  }

  private savePendingListenerHistory(): void {
    this.saveBroadcastMessagesToHistory(this.pendingListenerHistoryMessages);
    this.pendingListenerHistoryMessages = [];
  }

  private saveBroadcastMessagesToHistory(messages: BroadcastMessage[]): void {
    if (!messages?.length) {
      return;
    }

    const sortedMessages = [...messages].sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );

    let savedCount = 0;
    for (const message of sortedMessages) {
      if (message.targetLanguage !== this.selectedListenerLanguage) {
        continue;
      }

      const originalText = message.sourceText?.trim() || '';
      const translatedText = message.translatedText?.trim() || '';
      if (!originalText && !translatedText) {
        continue;
      }

      const historyKey = this.getBroadcastHistoryKey(
        originalText,
        translatedText,
        message.sourceLanguage || this.selectedSourceLanguage,
        message.targetLanguage || this.selectedListenerLanguage
      );

      if (this.savedBroadcastHistoryKeys.has(historyKey)) {
        continue;
      }

      this.savedBroadcastHistoryKeys.add(historyKey);
      this.conversationHistoryService.addConversation({
        originalText: originalText || translatedText,
        translatedText,
        userLanguage:
          message.sourceLanguage || this.selectedSourceLanguage || 'en',
        targetLanguage:
          message.targetLanguage || this.selectedListenerLanguage || 'en',
        confidence: 0,
        inputType: 'speech',
      });
      savedCount++;
    }

    if (savedCount > 0) {
      console.log('[Broadcast] 📝 Saved broadcast listener history:', {
        entryCount: savedCount,
      });
    }
  }

  /**
   * UTILITY
   */
  dismiss(): void {
    this.stopBroadcasting();
    this.leaveSession();
    this.close.emit();
  }

  setMode(mode: 'speaker' | 'listener'): void {
    this.mode = mode;
    this.errorMessage = '';
    this.cdr.markForCheck();
  }

  getLanguageName(languageCode: string): string {
    return (
      this.availableLanguages.find((lang) => lang.code === languageCode)
        ?.name || languageCode
    );
  }

  onBuyWords(): void {
    this.close.emit();
    this.showBuyWords.emit();
  }

  /**
   * Check if error message is a word limit error
   */
  isWordLimitError(errorMessage: string): boolean {
    if (!errorMessage) return false;

    // Check for English keywords (for backward compatibility)
    const englishKeywords = [
      'Word limit exceeded',
      'Please buy more words',
      'word limit',
    ];
    if (englishKeywords.some((keyword) => errorMessage.includes(keyword))) {
      return true;
    }

    // Check for Korean keywords
    const koreanKeywords = ['단어 제한', '단어를 더 구매', '단어 한도'];
    if (koreanKeywords.some((keyword) => errorMessage.includes(keyword))) {
      return true;
    }

    // Check for Russian keywords
    const russianKeywords = [
      'Превышен лимит слов',
      'купите больше слов',
      'лимит слов',
    ];
    if (russianKeywords.some((keyword) => errorMessage.includes(keyword))) {
      return true;
    }

    // Check for Chinese keywords
    const chineseKeywords = ['字数限制', '购买更多字数', '字数上限'];
    if (chineseKeywords.some((keyword) => errorMessage.includes(keyword))) {
      return true;
    }

    // Generic pattern: if error message contains numbers (word counts)
    // and mentions buying/continuing, it's likely a word limit error
    const hasWordCountPattern =
      /\d+.*\d+.*(?:out of|중|из|words|단어|слов|сөз)/i.test(errorMessage);
    const hasBuyContinuePattern =
      /(?:buy|구매|купить|сатып алыңыз|continue|계속|продолжить|улантуу)/i.test(
        errorMessage
      );

    if (hasWordCountPattern && hasBuyContinuePattern) {
      return true;
    }

    return false;
  }

  /**
   * Show toast notification
   */
  private async showToast(
    message: string,
    color: string = 'primary',
    duration: number = 3000
  ): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration,
      color,
      position: 'top',
      buttons: [
        {
          text: 'OK',
          role: 'cancel',
        },
      ],
    });
    await toast.present();
  }

  /**
   * Show word count info in a popover
   */
  async showSourceLanguageInfo(event: Event): Promise<void> {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    
    const message =
      this.translations.sourceLanguageSet ||
      'Source language is set from your section';

    const alert = await this.alertController.create({
      header: this.translations.sourceLanguage || 'Source Language',
      message: message,
      buttons: [
        {
          text: this.translations.ok || 'OK',
          role: 'cancel',
        },
      ],
    });
    await alert.present();
  }

  async showWordCountInfo(event: Event): Promise<void> {
    event.stopPropagation(); // Prevent button click
    const message =
      this.translations.wordCountWarning ||
      'Note: Each additional language will multiply word counts (e.g., 2 languages = 2x words, 3 languages = 3x words)';

    const alert = await this.alertController.create({
      header: this.translations.note || 'Note',
      message: message,
      buttons: [
        {
          text: this.translations.ok || 'OK',
          role: 'cancel',
        },
      ],
      cssClass: 'word-count-info-alert',
    });
    await alert.present();
  }

  /**
   * Show lecture mode info in an alert
   */
  async showLectureModeInfo(event: Event): Promise<void> {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    
    const message =
      this.translations.lectureModeDescription ||
      'Record every 20 seconds for better translation context';

    const alert = await this.alertController.create({
      header: this.translations.lectureMode || 'Lecture Mode',
      message: message,
      buttons: [
        {
          text: this.translations.ok || 'OK',
          role: 'cancel',
        },
      ],
    });
    await alert.present();
  }

  async showAdvancedModelInfo(event: Event): Promise<void> {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    const message =
      this.translations.advancedModelDescription ||
      'Uses gpt-5.4-mini. May be slower.';

    const alert = await this.alertController.create({
      header: this.translations.advancedModel || 'Advanced translation model',
      message,
      buttons: [
        {
          text: this.translations.ok || 'OK',
          role: 'cancel',
        },
      ],
    });
    await alert.present();
  }

  /**
   * TrackBy function for speaker source texts
   */
  trackSpeakerSourceText(
    index: number,
    item: { text: string; timestamp: Date }
  ): string {
    return `${item.timestamp.getTime()}-${item.text.substring(0, 20)}`;
  }

  /**
   * Format time for display
   */
  formatTime(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (seconds < 60) {
      return `${seconds}s ago`;
    } else if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return date.toLocaleTimeString();
    }
  }

  /**
   * Process a new source text for audio (called when new transcription arrives)
   */
  private async processSourceTextForAudio(sourceText: string): Promise<void> {
    if (!sourceText || !sourceText.trim()) {
      return;
    }

    // Skip if already processed (prevent duplicates)
    if (this.processedSourceTexts.has(sourceText)) {
      return;
    }

    this.processedSourceTexts.add(sourceText);

    try {
      console.log('[Broadcast] 🔊 Generating audio for source text:', {
        text: sourceText.substring(0, 50),
        sourceLanguage: this.selectedSourceLanguage,
      });

      // Generate TTS audio
      const audioResult = await this.translatorService.tts(
        sourceText,
        1.0, // Default speed
        'alloy', // Default voice
        this.selectedSourceLanguage
      );

      if (!audioResult || !audioResult.audioUrl) {
        console.warn('[Broadcast] 🔊 TTS failed: no audio URL returned');
        return;
      }

      // Add to queue
      this.audioQueue.push({
        audioUrl: audioResult.audioUrl,
        text: sourceText,
      });

      console.log(
        '[Broadcast] 🔊 Audio added to queue. Queue length:',
        this.audioQueue.length
      );

      // Try to play if not already playing
      if (!this.isPlayingAudio && this.audioEnabled()) {
        if (this.isIOSBrowser) {
          // For iOS: Try immediate playback
          Promise.resolve().then(() => {
            if (
              !this.isPlayingAudio &&
              this.audioQueue.length > 0 &&
              this.audioEnabled()
            ) {
              this.playNextAudio().catch((err) => {
                console.warn(
                  '[Broadcast] Immediate playback failed, audio remains in queue:',
                  err
                );
              });
            }
          });
        } else {
          // For non-iOS: Play next audio
          this.playNextAudio().catch((err) => {
            console.error('[Broadcast] Error playing audio:', err);
          });
        }
      }
    } catch (error) {
      console.error('[Broadcast] 🔊 Error generating audio:', error);
    }
  }

  /**
   * Process existing source texts for audio (when audio is enabled)
   */
  private async processExistingSourceTextsForAudio(): Promise<void> {
    // Process recent source texts (last 10) to avoid processing too many old ones
    const recentTexts = this.speakerSourceTexts.slice(-10);
    for (const item of recentTexts) {
      if (item.text && !this.processedSourceTexts.has(item.text)) {
        await this.processSourceTextForAudio(item.text);
      }
    }
  }

  /**
   * Get reversed source texts for display (latest first)
   */
  getReversedSourceTexts(): Array<{ text: string; timestamp: Date }> {
    return [...this.speakerSourceTexts].reverse();
  }

  /**
   * Get filtered messages for listener (only messages matching selected language)
   */
  getFilteredMessages(): BroadcastMessage[] {
    if (this.mode !== 'listener' || !this.isJoined) {
      return [];
    }
    // Filter messages to only show those matching the selected listener language
    return this.messages.filter(
      (msg) => msg.targetLanguage === this.selectedListenerLanguage
    );
  }
}
